"use client";

import { useEffect, useRef, useState } from "react";
import Icon from "@/src/components/ui/AppIcon";

interface DemoModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const DemoModal = ({ isOpen, onClose }: DemoModalProps) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
      videoRef.current?.pause();
      setIsPlaying(false);
    }
    return () => {
      document.body.style.overflow = "unset";
      videoRef.current?.pause();
      setIsPlaying(false);
    };
  }, [isOpen]);

  const handlePlayClick = () => {
    if (!videoRef.current) return;
    videoRef.current.muted = false; // Unmute
    videoRef.current.play(); // Start playing
    setIsPlaying(true);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      />

      <div className="relative bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <h3 className="text-2xl font-bold text-text-primary">
            Fillr Demo Video
          </h3>
          <button
            onClick={onClose}
            className="w-10 h-10 flex items-center justify-center rounded-lg hover:bg-surface transition-colors"
            aria-label="Close modal"
          >
            <Icon name="XMarkIcon" size={24} className="text-text-secondary" />
          </button>
        </div>

        <div className="relative bg-black aspect-video">
          <video
            ref={videoRef}
            className="absolute inset-0 w-full h-full object-contain"
            controls={isPlaying}
            muted
            preload="metadata"
          >
            <source src="/videos/fillr-demo.mp4" type="video/mp4" />
            Your browser does not support the video tag.
          </video>

          {!isPlaying && (
            <div
              className="absolute inset-0 flex flex-col items-center justify-center bg-black/50 cursor-pointer"
              onClick={handlePlayClick}
            >
              <Icon
                name="PlayCircleIcon"
                size={64}
                className="text-white"
                variant="solid"
              />
              <p className="text-white/80 mt-2 text-center">
                Click to play demo
              </p>
            </div>
          )}
        </div>

        <div className="p-6 bg-surface">
          <div className="grid grid-cols-3 gap-4">
            <div className="text-center">
              <Icon
                name="CameraIcon"
                size={24}
                className="text-primary mx-auto mb-2"
              />
              <p className="text-xs sm:text-sm md:text-base font-medium text-text-primary">
                Smart Receipt Capture
              </p>
            </div>
            <div className="text-center">
              <Icon
                name="MapPinIcon"
                size={24}
                className="text-primary mx-auto mb-2"
              />
              <p className="text-xs sm:text-sm md:text-base font-medium text-text-primary">
                GPS Trip Tracking
              </p>
            </div>
            <div className="text-center">
              <Icon
                name="ChartBarIcon"
                size={24}
                className="text-primary mx-auto mb-2"
              />
              <p className="text-xs sm:text-sm md:text-base font-medium text-text-primary">
                Earnings Analytics
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DemoModal;
